// src/features/shared/RouteTransition.jsx
//
// Trading-themed route transition. Intercepts in-app link clicks, fades a
// full-screen loader IN, holds, then navigates *under* the already-painted
// overlay (so the heavy incoming page can't starve the fade-in), then fades
// OUT to reveal it. Centered TI logo over a glowing price-line / sonar scope.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

const ENTER_MS  = 300;  // fade-in (CSS .ti-pt--active transition)
const HOLD_MS   = 820;  // fully-shown hold before navigating
const SETTLE_MS = 90;   // let the new page paint before fading out
const EXIT_MS   = 440;  // fade-out (CSS .ti-pt transition)

const LOGO = `${process.env.PUBLIC_URL}/images/logo/ti-reversed-logo.png`;

// An upward, jagged "price line" traced behind the logo (viewBox 0 0 720 180).
const LINE =
  '0,150 56,118 112,138 168,90 224,112 280,60 336,86 392,44 448,72 504,28 560,54 616,18 672,40 720,24';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const RouteTransition = () => {
  const history = useHistory();
  const [phase, setPhase] = useState('idle');   // idle | active | exit
  const timers = useRef([]);

  const clearTimers = useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  }, []);

  // Show loader → hold → navigate under it → settle → fade out.
  const go = useCallback((to) => {
    if (prefersReducedMotion()) { history.push(to); return; }
    clearTimers();
    setPhase('active');
    timers.current.push(setTimeout(() => {
      history.push(to);
      timers.current.push(setTimeout(() => setPhase('exit'), SETTLE_MS));
      timers.current.push(setTimeout(() => setPhase('idle'), SETTLE_MS + EXIT_MS));
    }, ENTER_MS + HOLD_MS));
  }, [history, clearTimers]);

  useEffect(() => {
    // Intercept clicks on internal links anywhere in the app.
    const onClick = (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target.closest && e.target.closest('a');
      if (!a) return;
      const target = a.getAttribute('target');
      if ((target && target !== '_self') || a.hasAttribute('download')) return;
      const href = a.getAttribute('href');
      if (!href || !href.startsWith('/') || href.startsWith('//')) return;
      let url;
      try { url = new URL(href, window.location.origin); } catch (_) { return; }
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname) return;   // same page → let it be
      // preventDefault also stops React Router <Link> from double-pushing
      // (its handler bails on event.defaultPrevented).
      e.preventDefault();
      go(url.pathname + url.search + url.hash);
    };

    // Programmatic navigations opt in via transitionToRoute() → this event.
    const onNav = (e) => { if (e.detail && e.detail.path) go(e.detail.path); };

    document.addEventListener('click', onClick, true);
    window.addEventListener('ti:navigate', onNav);
    return () => {
      document.removeEventListener('click', onClick, true);
      window.removeEventListener('ti:navigate', onNav);
      clearTimers();
    };
  }, [go, clearTimers]);

  return (
    <div className={`ti-pt ti-pt--${phase}`} aria-hidden="true" role="presentation">
      {phase !== 'idle' && (
        <>
          <div className="ti-pt__aurora" />
          <div className="ti-pt__grain" />

          <div className="ti-pt__stage">
            <span className="ti-pt__ring ti-pt__ring--1" />
            <span className="ti-pt__ring ti-pt__ring--2" />
            <span className="ti-pt__ring ti-pt__ring--3" />

            <svg
              className="ti-pt__chart"
              viewBox="0 0 720 180"
              fill="none"
              preserveAspectRatio="xMidYMid meet"
              aria-hidden="true"
            >
              <defs>
                <linearGradient id="ptLineGrad" x1="0" y1="0" x2="720" y2="0" gradientUnits="userSpaceOnUse">
                  <stop offset="0"   stopColor="rgb(45,212,255)" />
                  <stop offset="0.5" stopColor="rgb(139,92,246)" />
                  <stop offset="1"   stopColor="rgb(236,72,153)" />
                </linearGradient>
                <linearGradient id="ptAreaGrad" x1="0" y1="0" x2="0" y2="180" gradientUnits="userSpaceOnUse">
                  <stop offset="0" stopColor="rgba(139,92,246,0.20)" />
                  <stop offset="1" stopColor="rgba(45,212,255,0)" />
                </linearGradient>
              </defs>
              <polygon className="ti-pt__area" points={`${LINE} 720,180 0,180`} fill="url(#ptAreaGrad)" />
              <polyline
                className="ti-pt__line-base"
                points={LINE}
                stroke="url(#ptLineGrad)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <polyline
                className="ti-pt__line-flow"
                points={LINE}
                stroke="url(#ptLineGrad)"
                strokeWidth="2.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                pathLength="1000"
              />
            </svg>

            <img className="ti-pt__logo" src={LOGO} alt="" draggable="false" />
          </div>

          <div className="ti-pt__bar"><span className="ti-pt__bar-fill" /></div>
        </>
      )}
    </div>
  );
};

export default RouteTransition;
